// src/admin/pages/AdminOrders.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import {
    FaSearch,
    FaSort,
    FaSortUp,
    FaSortDown,
    FaEye,
    FaTrash,
} from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import { getAdminOrders, deleteAdminOrder } from "../../services/adminOrders.api";
import { FaGlobe, FaWhatsapp, FaStore } from "react-icons/fa";

function AdminOrders() {
    const [isOpen, setIsOpen] = useState(false);

    const [orders, setOrders] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [sourceFilter, setSourceFilter] = useState("All");
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState("asc");


    const [deleteId, setDeleteId] = useState(null);

    const navigate = useNavigate();
    const { lang } = useContext(AdminLanguageContext);

    const [totalCount, setTotalCount] = useState(0);
    const [showToast, setShowToast] = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    
    const [expandedStatus, setExpandedStatus] = useState({});

    const orderSourceIcon = (source) => {
  switch (source) {
    case "website":
      return <FaGlobe title="Website" className="text-cyan-600" />;
    case "whatsapp":
      return <FaWhatsapp title="WhatsApp" className="text-green-600" />;
    case "store":
      return <FaStore title="Store" className="text-gray-700" />;
    default:
      return null;
  }
};

    const TEXT = {
        EN: {
            title: "Orders",
            searchPlaceholder: "Search by Order ID or Customer...",
            all: "All",
            pending: "Pending",
            shipped: "Shipped",
            delivered: "Delivered",
            cancelled: "Cancelled",
            orderId: "Order ID",
            customer: "Customer",
            total: "Total",
            status: "Status",
            date: "Date",
            actions: "Actions",
            loading: "Loading orders...",
            noOrders: "No orders found.",
            view: "View",
            delete: "Delete",
            prev: "Prev",
            next: "Next",
            deleteTitle: "Delete Order?",
            deleteText: "This action cannot be undone.",
            cancel: "Cancel",
        },
        AR: {
            title: "الطلبات",
            searchPlaceholder: "بحث برقم الطلب أو اسم العميل...",
            all: "الكل",
            pending: "قيد الانتظار",
            shipped: "تم الشحن",
            delivered: "تم التسليم",
            cancelled: "ملغي",
            orderId: "رقم الطلب",
            customer: "العميل",
            total: "الإجمالي",
            status: "الحالة",
            date: "التاريخ",
            actions: "الإجراءات",
            loading: "جاري تحميل الطلبات...",
            noOrders: "لا توجد طلبات.",
            view: "عرض",
            delete: "حذف",
            prev: "السابق",
            next: "التالي",
            deleteTitle: "حذف الطلب؟",
            deleteText: "لا يمكن التراجع عن هذا الإجراء.",
            cancel: "إلغاء",
        },
    }[lang];

    const ORDER_STATUSES = [
  "pending",
  "paid",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
];


    const formatDate = (date) =>
  new Date(date).toLocaleDateString();

  const getOrderDateYMD = (date) => {
    if (!date) return "";
    const value = String(date);

    // Backend sends "YYYY-MM-DD HH:mm:ss", so this avoids timezone shift.
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const statusColor = (status) => {
  switch (status) {
    case "pending":
      return "bg-orange-100 text-orange-700";
    case "paid":
      return "bg-green-100 text-green-700";
    case "shipped":
      return "bg-blue-100 text-blue-700";
    case "delivered":
      return "bg-emerald-100 text-emerald-700";
    case "cancelled":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};


    useEffect(() => {
  let mounted = true;
  setLoading(true);

  getAdminOrders()
  .then((data) => {
    if (mounted) {
      setOrders(data.orders);     // ✅ array
     setTotalCount(data.orders.length);  // ✅ total count
    }
  })
    .catch(console.error)
    .finally(() => setLoading(false));

  return () => (mounted = false);
}, []);


    const filtered = orders.filter((o) => {
        const s = search.toLowerCase();

const matchesSearch =
  o.customerName.toLowerCase().includes(s) ||
  o.orderNumber.toLowerCase().includes(s) ||
  o.id.toString().includes(s) ||
  (o.customerPhone && o.customerPhone.toLowerCase().includes(s));



        const matchesStatus =
  statusFilter === "All" ? true : o.status === statusFilter;

const matchesSource =
  sourceFilter === "All" ? true : o.orderSource === sourceFilter;

const orderDateYMD = getOrderDateYMD(o.date);

const matchesFrom =
  fromDate ? orderDateYMD >= fromDate : true;

const matchesTo =
  toDate ? orderDateYMD <= toDate : true;

return (
  matchesSearch &&
  matchesStatus &&
  matchesSource &&
  matchesFrom &&
  matchesTo
);


    });

    const sorted = [...filtered].sort((a, b) => {
        if (!sortBy) return 0;

        let vA = a[sortBy];
        let vB = b[sortBy];

        if (sortBy === "date") {
            vA = new Date(vA);
            vB = new Date(vB);
        }

        if (sortDirection === "asc") return vA > vB ? 1 : -1;
        return vA < vB ? 1 : -1;
    });

    const ordersByStatus = ORDER_STATUSES.reduce((acc, status) => {
  acc[status] = sorted.filter((o) => o.status === status);
  return acc;
}, {});


    const changeSort = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDirection("asc");
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return <FaSort className="inline ml-1 opacity-40" />;
        return sortDirection === "asc" ? (
            <FaSortUp className="inline ml-1" />
        ) : (
            <FaSortDown className="inline ml-1" />
        );
    };

    const confirmDelete = async () => {
  try {
    await deleteAdminOrder(deleteId);
    setOrders((prev) => prev.filter((o) => o.id !== deleteId));
    setTotalCount((c) => c - 1);

    // ✅ show toast
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  } catch (e) {
    alert(e.message);
  } finally {
    setDeleteId(null);
  }
};

const resetFilters = () => {
  setSearch("");
  setStatusFilter("All");
  setSourceFilter("All");
  setFromDate("");
  setToDate("");
};


    return (
        <div className={`flex bg-gray-100 min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
            <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="flex-1 lg:ml-64 w-full transition-all duration-300">
                <AdminHeader setIsOpen={setIsOpen} />

                <main className="p-4 sm:p-6 animate-fadeSlide">

                    <div className="flex justify-between items-start mb-6">
  <div>
    <h1 className="text-2xl font-bold text-cyan-700">
      {TEXT.title}
    </h1>

    <p className="text-sm text-gray-500 mt-1">
  {lang === "EN"
  ? `${totalCount} orders`
  : `${totalCount} طلب`}
</p>

  </div>

  <button
    onClick={() => navigate("/admin/orders/add")}
    className="
      px-4 py-2
      bg-cyan-600 text-white
      rounded-xl shadow
      hover:bg-cyan-700
      transition
    "
  >
    + {lang === "EN" ? "Add Order" : "إضافة طلب"}
  </button>
</div>

                    {/* SEARCH + FILTERS */}
<div className="flex flex-wrap gap-4 mb-5">
  {/* Search (smaller now) */}
  <div className="relative flex-1 min-w-[180px] md:flex-[2]">
    <FaSearch className="absolute left-3 top-3 text-gray-400" />
    <input
      type="text"
      placeholder={TEXT.searchPlaceholder}
      className="w-full pl-10 p-3 border rounded-xl shadow-sm"
      value={search}
      onChange={(e) => {
        setSearch(e.target.value);
      }}
    />
  </div>

  {/* Source dropdown (new) */}
  <select
    className="p-3 rounded-xl border shadow-sm min-w-[140px]"
    value={sourceFilter}
    onChange={(e) => {
      setSourceFilter(e.target.value);
    }}
  >
    <option value="All">{lang === "EN" ? "All Sources" : "كل المصادر"}</option>
    <option value="website">{lang === "EN" ? "Website" : "الموقع"}</option>
    <option value="whatsapp">{lang === "EN" ? "WhatsApp" : "واتساب"}</option>
    <option value="store">{lang === "EN" ? "Store" : "المحل"}</option>
  </select>

  {/* Status dropdown (same but now sits after source) */}
  <select
    className="p-3 rounded-xl border shadow-sm min-w-[150px]"
    value={statusFilter}
    onChange={(e) => {
      setStatusFilter(e.target.value);
    }}
  >
    <option value="All">{TEXT.all}</option>
    <option value="pending">Pending</option>
    <option value="paid">Paid</option>
    <option value="shipped">Shipped</option>
    <option value="delivered">Delivered</option>
    <option value="cancelled">Cancelled</option>
    <option value="refunded">Refunded</option>
  </select>
  {/* DATE FILTER */}
<div className="flex items-center gap-3 bg-white border rounded-xl px-3 py-2 shadow-sm">
  <div className="flex flex-col">
    <span className="text-xs text-gray-500">
      {lang === "EN" ? "From" : "من"}
    </span>
    <input
  type="date"
  value={fromDate}
  onChange={(e) => {
    setFromDate(e.target.value);
  }}
  className="
    appearance-none
    bg-transparent
    text-sm font-medium text-gray-700
    px-2 py-1
    rounded-md
    focus:outline-none
    focus:ring-2 focus:ring-cyan-400
    focus:bg-white
    cursor-pointer
  "
/>
  </div>

  <span className="text-gray-400">—</span>

  <div className="flex flex-col">
    <span className="text-xs text-gray-500">
      {lang === "EN" ? "To" : "إلى"}
    </span>
    <input
  type="date"
  value={toDate}
  onChange={(e) => {
    setToDate(e.target.value);
  }}
  className="
    appearance-none
    bg-transparent
    text-sm font-medium text-gray-700
    px-2 py-1
    rounded-md
    focus:outline-none
    focus:ring-2 focus:ring-cyan-400
    focus:bg-white
    cursor-pointer
  "
/>

  </div>
</div>
{/* RESET FILTERS */}
<button
  onClick={resetFilters}
  className="
    px-4 py-2
    rounded-xl
    border
    text-sm font-medium
    text-gray-600
    bg-white
    hover:bg-gray-100
    hover:text-gray-800
    transition
  "
>
  {lang === "EN" ? "Reset" : "إعادة تعيين"}
</button>

</div>

{/* FILTERED COUNT */}
<div className="mb-4">
  <span className="inline-flex items-center gap-2 px-4 py-1.5
                   bg-cyan-50 text-cyan-700
                   border border-cyan-200
                   rounded-full text-sm font-medium">
    📊
    {lang === "EN"
      ? `${filtered.length} orders found`
      : `تم العثور على ${filtered.length} طلب`}
  </span>
</div>



                    {/* MOBILE CARDS */}
<div className="md:hidden space-y-4">
  {loading ? (
    <div className="text-center py-6">{TEXT.loading}</div>
  ) : filtered.length === 0 ? (
    <div className="text-center py-6">{TEXT.noOrders}</div>
  ) : (
    filtered.map((o) => (
      <div
        key={o.id}
        className="bg-white rounded-xl shadow p-4 border"
      >
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold text-cyan-700">
  #{o.id} <span className="text-gray-500">({o.orderNumber})</span>
</span>

          <span className="text-sm text-gray-500">
            {formatDate(o.date)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-1 text-sm">
  <span className="text-gray-500">Source:</span>
  {orderSourceIcon(o.orderSource)}
</div>


        <p className="text-sm">
  <b>{TEXT.customer}:</b> {o.customerName}
  {o.customerPhone && ` • ${o.customerPhone}`}
</p>


        <p className="text-sm">
  <b>{TEXT.total}:</b>{" "}
  {o.extraFeeAmount > 0 ? (
    <span>
      <span className="block text-xs text-gray-400">
        ${(o.total - o.extraFeeAmount).toFixed(2)}
      </span>
      <span className="block text-xs text-green-600">
        + {o.extraFeeLabel || "extra"} ${o.extraFeeAmount.toFixed(2)}
      </span>
      <span className="block font-bold">
        ${o.total.toFixed(2)}
      </span>
    </span>
  ) : (
    `$${o.total.toFixed(2)}`
  )}
</p>


        <span
  className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-semibold ${statusColor(o.status)}`}
>
  {o.status}
</span>

<div className="flex items-center gap-2 mt-1 text-sm">
  <span className="text-gray-500">
    {lang === "EN" ? "Shipping:" : "الشحن:"}
  </span>
  <span
    className={`font-semibold
      ${o.shippingMethod === "pickup"
        ? "text-green-600"
        : "text-blue-600"}
    `}
  >
    {o.shippingMethod === "pickup"
      ? lang === "EN"
        ? "Pickup"
        : "استلام"
      : lang === "EN"
      ? "Delivery"
      : "توصيل"}
  </span>
</div>


        <div className="flex justify-end gap-4 mt-3">
          <button
            className="text-cyan-600"
            onClick={() => navigate(`/admin/orders/${o.id}`)}
          >
            <FaEye />
          </button>

          <button
            className="text-red-600"
            onClick={() => setDeleteId(o.id)}
          >
            <FaTrash />
          </button>
        </div>
      </div>
    ))
  )}
</div>


                    {/* DESKTOP TABLES PER STATUS */}
<div className="hidden md:block space-y-8">
  {ORDER_STATUSES.map((status) => {
    const rows = ordersByStatus[status];
    const isExpanded = expandedStatus[status];
    const visibleRows = isExpanded ? rows : rows.slice(0, 10);


    if (!rows.length) return null;

    return (
      <div key={status} className="bg-white shadow-lg rounded-xl overflow-hidden">
        {/* STATUS HEADER */}
        <div className="flex justify-between items-center px-5 py-3 bg-gray-50 border-b">
          <h2 className="font-bold text-gray-800 capitalize">
            {lang === "EN" ? status : TEXT[status] || status}
          </h2>

          <span className="text-sm px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
            {rows.length} {lang === "EN" ? "orders" : "طلب"}
          </span>
        </div>

        {/* SAME TABLE STRUCTURE */}
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-200 text-gray-700">
            <tr>
              <th className="p-3">{TEXT.orderId}</th>
              <th className="p-3">{TEXT.customer}</th>
              <th className="p-3">{TEXT.total}</th>
              <th className="p-3">{TEXT.status}</th>
              <th className="p-3">{TEXT.date}</th>
<th className="p-3 text-center">
  {lang === "EN" ? "Shipping" : "طريقة الشحن"}
</th>
<th className="p-3 text-center">Src</th>
<th className="p-3">
  {lang === "EN" ? "Admin Note" : "ملاحظة الإدارة"}
</th>
<th className="p-3 text-center">{TEXT.actions}</th>

            </tr>
          </thead>

          <tbody>
            {visibleRows.map((o) => (
              <tr key={o.id} className="border-b hover:bg-gray-50">
                <td className="p-3 font-semibold">
                  {o.id} <span className="text-gray-500">({o.orderNumber})</span>
                </td>

                <td className="p-3">
                  <div className="font-medium">{o.customerName}</div>
                  {o.customerPhone && (
                    <div className="text-sm text-gray-500">
                      {o.customerPhone}
                    </div>
                  )}
                </td>

                <td className="p-3">
  {o.extraFeeAmount > 0 ? (
    <div className="leading-tight">
      {/* Old total */}
      <div className="text-xs text-gray-400">
        ${(o.total - o.extraFeeAmount).toFixed(2)}
      </div>

      {/* Extra fee */}
      <div className="text-xs text-green-600">
        + {o.extraFeeLabel || "extra"} ${o.extraFeeAmount.toFixed(2)}
      </div>

      {/* Final total */}
      <div className="font-bold text-black">
        ${o.total.toFixed(2)}
      </div>
    </div>
  ) : (
    <span className="font-medium">
      ${o.total.toFixed(2)}
    </span>
  )}
</td>


                <td className="p-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor(
                      o.status
                    )}`}
                  >
                    {o.status}
                  </span>
                </td>

                <td className="p-3">{formatDate(o.date)}</td>
                <td className="p-3 text-center">
  <span
    className={`px-3 py-1 rounded-full text-xs font-semibold
      ${o.shippingMethod === "pickup"
        ? "bg-green-100 text-green-700"
        : "bg-blue-100 text-blue-700"}
    `}
  >
    {o.shippingMethod === "pickup"
      ? lang === "EN"
        ? "Pickup"
        : "استلام"
      : lang === "EN"
      ? "Delivery"
      : "توصيل"}
  </span>
</td>

                <td className="p-3 text-center">
                  {orderSourceIcon(o.orderSource)}
                </td>
                
                <td className="p-3 max-w-[220px]">
  {o.adminNote ? (
    <span
      className="block truncate text-sm text-gray-700"
      title={o.adminNote}
    >
      {o.adminNote}
    </span>
  ) : (
    <span className="text-xs text-gray-400 italic">
      {lang === "EN" ? "—" : "—"}
    </span>
  )}
</td>

                <td className="p-3 flex justify-center gap-3">
                  <button
                    className="text-cyan-600"
                    onClick={() => navigate(`/admin/orders/${o.id}`)}
                  >
                    <FaEye />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => setDeleteId(o.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 10 && (
  <div className="p-4 border-t bg-gray-50 flex justify-center">
    <button
      onClick={() =>
        setExpandedStatus((prev) => ({
          ...prev,
          [status]: !prev[status],
        }))
      }
      className="
        px-5 py-2
        rounded-xl
        border
        text-sm font-medium
        bg-white
        hover:bg-gray-100
        transition
      "
    >
      {expandedStatus[status]
        ? lang === "EN"
          ? "Show less"
          : "عرض أقل"
        : lang === "EN"
        ? `Show all (${rows.length})`
        : `عرض الكل (${rows.length})`}
    </button>
  </div>
)}

      </div>
    );
  })}
</div>

                    
                    {/* DELETE MODAL */}
                    {deleteId !== null && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center">
                            <div className="bg-white p-6 rounded-xl shadow-xl w-80">
                                <h2 className="text-lg font-bold text-red-600 mb-3">
                                    {TEXT.deleteTitle}
                                </h2>
                                <p className="mb-6">{TEXT.deleteText}</p>

                                <div className="flex justify-end gap-3">
                                    <button
                                        className="px-4 py-2 bg-gray-300 rounded-lg"
                                        onClick={() => setDeleteId(null)}
                                    >
                                        {TEXT.cancel}
                                    </button>
                                    <button
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg"
                                        onClick={confirmDelete}
                                    >
                                        {TEXT.delete}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
               {showToast && (
  <div className="fixed bottom-6 right-6 z-[9999] animate-fadeSlide">
    <div className="bg-green-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3">
      <span className="text-lg">✅</span>
      <span className="text-sm font-medium">
        {lang === "EN" ? "Order deleted successfully" : "تم حذف الطلب بنجاح"}
      </span>
    </div>
  </div>
)}

                </main>
            </div>
        </div>
    );
}

export default AdminOrders;